/**
 * FundDocumentSection Component
 *
 * Expandable accordion section showing documents for a single fund,
 * including nested portfolio company documents
 */

import { useState } from 'react';
import { ChevronDown, ChevronRight, Folder, FolderOpen } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { FUND_DOCUMENT_FOLDERS } from '@/components/documents/fundDocumentFolders';
import {
  DocumentFolderTree,
  type FolderInitialState,
} from './DocumentFolderTree';
import { DocumentRow } from './DocumentRow';
import type {
  FundDocuments,
  PortalDocument,
  PortfolioCompanyDocuments,
} from '../services/documents-service';

interface DocumentGroupProps {
  title: string;
  documents: PortalDocument[];
  onDownload: (documentId: string, filename: string) => void;
  downloadingId: string | null;
  initialFolderState: FolderInitialState;
}

function DocumentGroup({
  title,
  documents,
  onDownload,
  downloadingId,
  initialFolderState,
}: DocumentGroupProps) {
  const [isExpanded, setIsExpanded] = useState(
    initialFolderState === 'expanded'
  );

  if (documents.length === 0) return null;
  return (
    <div>
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
        className="w-full flex items-center gap-2 py-2 px-2 rounded-md hover:bg-muted/50 transition-colors"
      >
        {isExpanded ? (
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
        )}
        {isExpanded ? (
          <FolderOpen className="h-4 w-4 text-muted-foreground" />
        ) : (
          <Folder className="h-4 w-4 text-muted-foreground" />
        )}
        <span className="text-sm font-medium text-foreground">{title}</span>
        <span className="text-xs text-muted-foreground">
          ({documents.length})
        </span>
      </button>
      {isExpanded && (
        <div className="ml-4">
          {documents.map((doc) => (
            <DocumentRow
              key={doc.id}
              document={doc}
              onDownload={onDownload}
              isDownloading={downloadingId === doc.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface PortfolioCompanySectionProps {
  portfolioCompany: PortfolioCompanyDocuments;
  onDownload: (documentId: string, filename: string) => void;
  downloadingId: string | null;
  initialFolderState: FolderInitialState;
}

function PortfolioCompanySection({
  portfolioCompany,
  onDownload,
  downloadingId,
  initialFolderState,
}: PortfolioCompanySectionProps) {
  const [isExpanded, setIsExpanded] = useState(
    initialFolderState === 'expanded'
  );

  return (
    <div className="ml-4">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-2 py-2 hover:bg-muted/50 rounded-md transition-colors px-2"
      >
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        )}
        <span className="text-sm font-medium text-foreground">
          {portfolioCompany.portfolio_company_name}
        </span>
        <span className="text-xs text-muted-foreground">
          ({portfolioCompany.document_count})
        </span>
      </button>

      {isExpanded && (
        <div className="ml-6">
          {portfolioCompany.documents.map((doc) => (
            <DocumentRow
              key={doc.id}
              document={doc}
              onDownload={onDownload}
              isDownloading={downloadingId === doc.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface FundDocumentSectionProps {
  fund: FundDocuments;
  onDownload: (documentId: string, filename: string) => void;
  downloadingId: string | null;
  initialFolderState: FolderInitialState;
}

export function FundDocumentSection({
  fund,
  onDownload,
  downloadingId,
  initialFolderState,
}: FundDocumentSectionProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  // Calculate total documents including portfolio companies
  const totalFundDocs = fund.document_count;
  const totalPortfolioDocs = (fund.portfolio_companies || []).reduce(
    (sum, pc) => sum + pc.document_count,
    0
  );
  const totalDocuments = totalFundDocs + totalPortfolioDocs;
  const fundLevelDocuments = fund.documents.filter((doc) => !doc.context_type);

  return (
    <Card>
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-3 p-4 hover:bg-muted/50 transition-colors text-left"
      >
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        )}
        {isExpanded ? (
          <FolderOpen className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        ) : (
          <Folder className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        )}
        <span className="font-medium text-foreground">{fund.fund_name}</span>
        <span className="text-sm text-muted-foreground ml-auto">
          {totalDocuments} {totalDocuments === 1 ? 'document' : 'documents'}
        </span>
      </button>

      {isExpanded && (
        <CardContent className="pt-0 pb-3">
          <DocumentGroup
            title="Fund Documents"
            documents={fundLevelDocuments.filter((doc) => !doc.folder_id)}
            onDownload={onDownload}
            downloadingId={downloadingId}
            initialFolderState={initialFolderState}
          />
          <DocumentFolderTree
            folders={fund.folders}
            documents={fundLevelDocuments}
            onDownload={onDownload}
            downloadingId={downloadingId}
            initialFolderState={initialFolderState}
          />
          {FUND_DOCUMENT_FOLDERS.map((folder) => (
            <DocumentGroup
              key={folder.contextType}
              title={folder.name}
              documents={fund.documents.filter(
                (doc) => doc.context_type === folder.contextType
              )}
              onDownload={onDownload}
              downloadingId={downloadingId}
              initialFolderState={initialFolderState}
            />
          ))}

          {/* Portfolio Company documents */}
          {fund.portfolio_companies && fund.portfolio_companies.length > 0 && (
            <div className={fund.documents.length > 0 ? 'mt-3' : ''}>
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide py-2 px-2">
                Portfolio Companies
              </div>
              {fund.portfolio_companies.map((pc) => (
                <PortfolioCompanySection
                  key={pc.portfolio_company_id}
                  portfolioCompany={pc}
                  onDownload={onDownload}
                  downloadingId={downloadingId}
                  initialFolderState={initialFolderState}
                />
              ))}
            </div>
          )}

          {/* Empty state when no documents */}
          {fund.documents.length === 0 &&
            (!fund.portfolio_companies ||
              fund.portfolio_companies.length === 0) && (
              <div className="text-sm text-muted-foreground py-4 text-center">
                No documents available
              </div>
            )}
        </CardContent>
      )}
    </Card>
  );
}
